/**

 * DH Commerce OS v1.4

 * Experiment Engine

 *

 * Generic, dependency-aware experimentation for Shopify storefronts.

 * No storefront-specific logic. No DOM mutation. Dormant until initialized.

 */

(function (global) {

  "use strict";

 

  var root = global.DHCommerce = global.DHCommerce || {};

  var ENGINE_NAME = "experimentEngine";

  var VERSION = "1.4.0";

  var STORAGE_KEY = "dhcommerce:experiments:v1";

  var SESSION_KEY = "dhcommerce:experiment-session:v1";

 

  var registry = new Map();

  var assignments = new Map();

  var exposures = new Map();

  var conversions = [];

  var initialized = false;

  var options = {};

 

  var DEFAULTS = {

    enabled: false,

    debug: false,

    persistAssignments: true,

    persistExposures: true,

    storageKey: STORAGE_KEY,

    sessionKey: SESSION_KEY,

    assignmentScope: "visitor",

    autoExposeOnAssign: false,

    emitDocumentEvents: true,

    maxConversionHistory: 250,

    visitorIdProvider: null,

    segmentResolver: null,

    contextProvider: null,

    nowProvider: null,

    randomProvider: null

  };

 

  function now() {

    return typeof options.nowProvider === "function"

      ? Number(options.nowProvider())

      : Date.now();

  }

 

  function log() {

    if (!options.debug || !global.console) return;

    var args = Array.prototype.slice.call(arguments);

    args.unshift("[DHCommerce experiments]");

    global.console.log.apply(global.console, args);

  }

 

  function warn() {

    if (!global.console) return;

    var args = Array.prototype.slice.call(arguments);

    args.unshift("[DHCommerce experiments]");

    global.console.warn.apply(global.console, args);

  }

 

  function isObject(value) {

    return value !== null && typeof value === "object" && !Array.isArray(value);

  }

 

  function clone(value) {

    if (value === undefined) return undefined;

    try {

      return JSON.parse(JSON.stringify(value));

    } catch (error) {

      return value;

    }

  }

 

  function merge(target) {

    var output = isObject(target) ? target : {};

    for (var i = 1; i < arguments.length; i += 1) {

      var source = arguments[i];

      if (!isObject(source)) continue;

      Object.keys(source).forEach(function (key) {

        if (isObject(source[key])) {

          output[key] = merge(isObject(output[key]) ? output[key] : {}, source[key]);

        } else {

          output[key] = source[key];

        }

      });

    }

    return output;

  }

 

  function safeParse(raw, fallback) {

    if (!raw) return fallback;

    try { return JSON.parse(raw); } catch (error) { return fallback; }

  }

 

  function storage(type) {

    try {

      return type === "session" ? global.sessionStorage : global.localStorage;

    } catch (error) {

      return null;

    }

  }

 

  function readStore(type, key, fallback) {

    var store = storage(type);

    if (!store) return fallback;

    try { return safeParse(store.getItem(key), fallback); } catch (error) { return fallback; }

  }

 

  function writeStore(type, key, value) {

    var store = storage(type);

    if (!store) return false;

    try {

      store.setItem(key, JSON.stringify(value));

      return true;

    } catch (error) {

      warn("Unable to persist experiment data", error);

      return false;

    }

  }

 

  function removeStore(type, key) {

    var store = storage(type);

    if (!store) return false;

    try { store.removeItem(key); return true; } catch (error) { return false; }

  }

 

  function emit(name, detail) {

    var payload = merge({}, detail || {}, {

      engine: ENGINE_NAME,

      engineVersion: VERSION,

      timestamp: now()

    });

 

    if (root.events && typeof root.events.emit === "function") {

      try { root.events.emit(name, payload); } catch (error) { warn("Event bus error", error); }

    }

 

    if (options.emitDocumentEvents && global.document && typeof global.CustomEvent === "function") {

      try {

        global.document.dispatchEvent(new CustomEvent("dhcommerce:" + name, { detail: payload }));

      } catch (error) {

        warn("Document event error", error);

      }

    }

 

    if (root.analytics && typeof root.analytics.track === "function") {

      try { root.analytics.track(name, payload); } catch (error) { log("Analytics track skipped", error); }

    }

 

    return payload;

  }

 

  function hashString(input) {

    var str = String(input || "");

    var hash = 2166136261;

    for (var i = 0; i < str.length; i += 1) {

      hash ^= str.charCodeAt(i);

      hash += (hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24);

    }

    return hash >>> 0;

  }

 

  function deterministicUnit(seed) {

    return hashString(seed) / 4294967296;

  }

 

  function generateId(prefix) {

    var random = typeof options.randomProvider === "function" ? options.randomProvider() : Math.random();

    return (prefix || "id") + "_" + now().toString(36) + "_" + Math.floor(random * 1e9).toString(36);

  }

 

  function getVisitorId() {

    if (typeof options.visitorIdProvider === "function") {

      var supplied = options.visitorIdProvider();

      if (supplied) return String(supplied);

    }

 

    if (root.state && root.state.visitor && root.state.visitor.id) {

      return String(root.state.visitor.id);

    }

 

    var session = readStore("session", options.sessionKey, {});

    if (!session.visitorId) {

      session.visitorId = generateId("visitor");

      writeStore("session", options.sessionKey, session);

    }

    return session.visitorId;

  }

 

  function getContext(extra) {

    var context = {};

    if (root.state) context.state = clone(root.state);

    if (root.personalizationEngine && typeof root.personalizationEngine.getProfile === "function") {

      try { context.profile = clone(root.personalizationEngine.getProfile()); } catch (error) { /* noop */ }

    }

    if (typeof options.contextProvider === "function") {

      try { context = merge(context, options.contextProvider() || {}); } catch (error) { warn("Context provider failed", error); }

    }

    return merge(context, extra || {});

  }

 

  function normalizeVariant(variant, index) {

    if (typeof variant === "string") {

      return { id: variant, name: variant, weight: 1, index: index, payload: {} };

    }

    if (!isObject(variant) || !variant.id) throw new Error("Each experiment variant requires an id");

    return {

      id: String(variant.id),

      name: variant.name || String(variant.id),

      weight: Number.isFinite(Number(variant.weight)) ? Math.max(0, Number(variant.weight)) : 1,

      index: index,

      payload: clone(variant.payload || {}),

      metadata: clone(variant.metadata || {})

    };

  }

 

  function normalizeGoal(goal) {

    if (typeof goal === "string") return { id: goal, event: goal, primary: false };

    if (!isObject(goal) || !goal.id) throw new Error("Each experiment goal requires an id");

    return {

      id: String(goal.id),

      event: goal.event || goal.id,

      primary: Boolean(goal.primary),

      oncePerAssignment: goal.oncePerAssignment !== false,

      valuePath: goal.valuePath || null,

      metadata: clone(goal.metadata || {})

    };

  }

 

  function normalizeExperiment(input) {

    if (!isObject(input) || !input.id) throw new Error("Experiment requires an id");

    var variants = (input.variants || []).map(normalizeVariant);

    if (variants.length < 2) throw new Error("Experiment requires at least two variants");

    var ids = new Set();

    variants.forEach(function (variant) {

      if (ids.has(variant.id)) throw new Error("Duplicate variant id: " + variant.id);

      ids.add(variant.id);

    });

 

    var allocation = Number.isFinite(Number(input.trafficAllocation))

      ? Math.min(1, Math.max(0, Number(input.trafficAllocation)))

      : 1;

 

    return {

      id: String(input.id),

      name: input.name || String(input.id),

      description: input.description || "",

      status: input.status || "draft",

      version: input.version || 1,

      variants: variants,

      goals: (input.goals || []).map(normalizeGoal),

      trafficAllocation: allocation,

      salt: input.salt || String(input.id),

      startAt: input.startAt ? new Date(input.startAt).getTime() : null,

      endAt: input.endAt ? new Date(input.endAt).getTime() : null,

      segments: clone(input.segments || []),

      excludeSegments: clone(input.excludeSegments || []),

      audience: typeof input.audience === "function" ? input.audience : null,

      override: clone(input.override || null),

      metadata: clone(input.metadata || {}),

      assignmentScope: input.assignmentScope || null,

      autoExpose: input.autoExpose,

      allowReassignment: Boolean(input.allowReassignment),

      namespace: input.namespace || "default",

      registeredAt: now()

    };

  }

 

  function register(input) {

    var experiment = normalizeExperiment(input);

    registry.set(experiment.id, experiment);

    emit("experiment-registered", { experimentId: experiment.id, experiment: clone(experiment) });

    return clone(experiment);

  }

 

  function registerMany(items) {

    if (!Array.isArray(items)) throw new TypeError("registerMany expects an array");

    return items.map(register);

  }

 

  function remove(id) {

    var key = String(id);

    var existed = registry.delete(key);

    if (existed) emit("experiment-removed", { experimentId: key });

    return existed;

  }

 

  function get(id) {

    var item = registry.get(String(id));

    return item ? clone(item) : null;

  }

 

  function getAll() {

    return Array.from(registry.values()).map(clone);

  }

 

  function clearRegistry() {

    registry.clear();

    emit("experiments-cleared", {});

    return true;

  }

 

  function isLive(experiment, timestamp) {

    if (!experiment || experiment.status !== "running") return false;

    var time = timestamp || now();

    if (experiment.startAt && time < experiment.startAt) return false;

    if (experiment.endAt && time >= experiment.endAt) return false;

    return true;

  }

 

  function resolveSegment(segment, context) {

    if (typeof options.segmentResolver === "function") {

      try { return Boolean(options.segmentResolver(segment, context)); } catch (error) { return false; }

    }

    if (root.ruleEngine && typeof root.ruleEngine.evaluate === "function") {

      try {

        var result = root.ruleEngine.evaluate(segment, context);

        return Boolean(result && (result.passed === true || result.result === true || result === true));

      } catch (error) { return false; }

    }

    if (typeof segment === "function") {

      try { return Boolean(segment(context)); } catch (error) { return false; }

    }

    return false;

  }

 

  function isAudienceEligible(experiment, context) {

    if (experiment.excludeSegments.some(function (s) { return resolveSegment(s, context); })) return false;

    if (experiment.segments.length && !experiment.segments.some(function (s) { return resolveSegment(s, context); })) return false;

    if (experiment.audience) {

      try { if (!experiment.audience(context)) return false; } catch (error) { return false; }

    }

    return true;

  }

 

  function assignmentKey(experiment, subjectId) {

    return experiment.namespace + ":" + experiment.id + ":" + subjectId;

  }

 

  function chooseVariant(experiment, subjectId) {

    if (experiment.override && experiment.override.variantId) {

      var forced = experiment.variants.find(function (v) { return v.id === String(experiment.override.variantId); });

      if (forced) return { variant: forced, reason: "override" };

    }

 

    var totalWeight = experiment.variants.reduce(function (sum, variant) { return sum + variant.weight; }, 0);

    if (totalWeight <= 0) return null;

    var point = deterministicUnit(experiment.salt + ":variant:" + subjectId) * totalWeight;

    var cursor = 0;

    for (var i = 0; i < experiment.variants.length; i += 1) {

      cursor += experiment.variants[i].weight;

      if (point < cursor) return { variant: experiment.variants[i], reason: "weighted-deterministic" };

    }

    return { variant: experiment.variants[experiment.variants.length - 1], reason: "weighted-fallback" };

  }

 

  function subjectFor(experiment, context) {

    var scope = experiment.assignmentScope || options.assignmentScope;

    if (scope === "session") {

      var session = readStore("session", options.sessionKey, {});

      if (!session.sessionId) {

        session.sessionId = generateId("session");

        writeStore("session", options.sessionKey, session);

      }

      return session.sessionId;

    }

    if (scope === "customer" && context && context.customer && context.customer.id) {

      return String(context.customer.id);

    }

    return getVisitorId();

  }

 

  function saveAssignments() {

    if (!options.persistAssignments) return;

    writeStore("local", options.storageKey, {

      assignments: Array.from(assignments.entries()),

      exposures: options.persistExposures ? Array.from(exposures.entries()) : [],

      conversions: conversions.slice(-options.maxConversionHistory),

      savedAt: now(),

      version: VERSION

    });

  }

 

  function hydrate() {

    var data = readStore("local", options.storageKey, {});

    if (Array.isArray(data.assignments)) assignments = new Map(data.assignments);

    if (Array.isArray(data.exposures)) exposures = new Map(data.exposures);

    if (Array.isArray(data.conversions)) conversions = data.conversions.slice(-options.maxConversionHistory);

    emit("experiments-hydrated", {

      assignmentCount: assignments.size,

      exposureCount: exposures.size,

      conversionCount: conversions.length

    });

    return diagnostics();

  }

 

  function assign(id, extraContext) {

    var experiment = registry.get(String(id));

    if (!experiment) return null;

    var context = getContext(extraContext);

    var timestamp = now();

 

    if (!options.enabled || !isLive(experiment, timestamp) || !isAudienceEligible(experiment, context)) {

      emit("experiment-ineligible", { experimentId: experiment.id });

      return null;

    }

 

    var subjectId = subjectFor(experiment, context);

    var key = assignmentKey(experiment, subjectId);

    var existing = assignments.get(key);

    if (existing && !experiment.allowReassignment && existing.experimentVersion === experiment.version) {

      if (experiment.autoExpose === true || (experiment.autoExpose !== false && options.autoExposeOnAssign)) {

        expose(experiment.id, { assignment: existing, context: context });

      }

      return clone(existing);

    }

 

    var trafficPoint = deterministicUnit(experiment.salt + ":traffic:" + subjectId);

    if (trafficPoint >= experiment.trafficAllocation) {

      emit("experiment-not-allocated", { experimentId: experiment.id, subjectId: subjectId });

      return null;

    }

 

    var selected = chooseVariant(experiment, subjectId);

    if (!selected) return null;

 

    var assignment = {

      assignmentId: generateId("assignment"),

      experimentId: experiment.id,

      experimentVersion: experiment.version,

      variantId: selected.variant.id,

      variantName: selected.variant.name,

      variantPayload: clone(selected.variant.payload),

      subjectId: subjectId,

      namespace: experiment.namespace,

      reason: selected.reason,

      assignedAt: timestamp,

      contextSnapshot: clone(context.assignmentContext || {})

    };

 

    assignments.set(key, assignment);

    saveAssignments();

    emit("experiment-assigned", clone(assignment));

 

    if (experiment.autoExpose === true || (experiment.autoExpose !== false && options.autoExposeOnAssign)) {

      expose(experiment.id, { assignment: assignment, context: context });

    }

    return clone(assignment);

  }

 

  function expose(id, input) {

    var experiment = registry.get(String(id));

    if (!experiment) return null;

    var data = input || {};

    var assignment = data.assignment || assign(id, data.context);

    if (!assignment) return null;

    var key = assignment.assignmentId;

    var existing = exposures.get(key);

    if (existing) return clone(existing);

 

    var exposure = {

      exposureId: generateId("exposure"),

      assignmentId: assignment.assignmentId,

      experimentId: assignment.experimentId,

      experimentVersion: assignment.experimentVersion,

      variantId: assignment.variantId,

      subjectId: assignment.subjectId,

      placement: data.placement || null,

      exposedAt: now(),

      metadata: clone(data.metadata || {})

    };

    exposures.set(key, exposure);

    saveAssignments();

    emit("experiment-exposed", clone(exposure));

    return clone(exposure);

  }

 

  function getAssignment(id, extraContext) {

    var experiment = registry.get(String(id));

    if (!experiment) return null;

    var subjectId = subjectFor(experiment, getContext(extraContext));

    var item = assignments.get(assignmentKey(experiment, subjectId));

    return item ? clone(item) : null;

  }

 

  function getAssignments() {

    return Array.from(assignments.values()).map(clone);

  }

 

  function recordConversion(input) {

    if (!isObject(input) || !input.goalId) throw new TypeError("recordConversion requires goalId");

    var experimentIds = input.experimentId ? [String(input.experimentId)] : Array.from(registry.keys());

    var recorded = [];

 

    experimentIds.forEach(function (experimentId) {

      var experiment = registry.get(experimentId);

      if (!experiment) return;

      var goal = experiment.goals.find(function (g) {

        return g.id === String(input.goalId) || g.event === String(input.goalId);

      });

      if (!goal) return;

      var assignment = input.assignment || getAssignment(experimentId, input.context);

      if (!assignment) return;

 

      var duplicate = goal.oncePerAssignment && conversions.some(function (item) {

        return item.assignmentId === assignment.assignmentId && item.goalId === goal.id;

      });

      if (duplicate) return;

 

      var conversion = {

        conversionId: generateId("conversion"),

        assignmentId: assignment.assignmentId,

        experimentId: experiment.id,

        experimentVersion: assignment.experimentVersion,

        variantId: assignment.variantId,

        subjectId: assignment.subjectId,

        goalId: goal.id,

        event: input.event || goal.event,

        value: Number.isFinite(Number(input.value)) ? Number(input.value) : null,

        currency: input.currency || null,

        orderId: input.orderId || null,

        convertedAt: now(),

        metadata: clone(input.metadata || {})

      };

      conversions.push(conversion);

      if (conversions.length > options.maxConversionHistory) {

        conversions = conversions.slice(-options.maxConversionHistory);

      }

      emit("experiment-converted", clone(conversion));

      recorded.push(conversion);

    });

 

    saveAssignments();

    return recorded.map(clone);

  }

 

  function trackEvent(eventName, payload) {

    var results = [];

    registry.forEach(function (experiment) {

      experiment.goals.forEach(function (goal) {

        if (goal.event === eventName) {

          results = results.concat(recordConversion({

            experimentId: experiment.id,

            goalId: goal.id,

            event: eventName,

            value: payload && payload.value,

            currency: payload && payload.currency,

            orderId: payload && payload.orderId,

            metadata: payload

          }));

        }

      });

    });

    return results;

  }

 

  function summary(id) {

    var experiment = registry.get(String(id));

    if (!experiment) return null;

    var rows = experiment.variants.map(function (variant) {

      var variantAssignments = Array.from(assignments.values()).filter(function (a) {

        return a.experimentId === experiment.id && a.variantId === variant.id;

      });

      var variantExposures = Array.from(exposures.values()).filter(function (e) {

        return e.experimentId === experiment.id && e.variantId === variant.id;

      });

      var variantConversions = conversions.filter(function (c) {

        return c.experimentId === experiment.id && c.variantId === variant.id;

      });

      return {

        variantId: variant.id,

        assignments: variantAssignments.length,

        exposures: variantExposures.length,

        conversions: variantConversions.length,

        conversionRate: variantExposures.length ? variantConversions.length / variantExposures.length : 0,

        totalValue: variantConversions.reduce(function (sum, c) { return sum + (c.value || 0); }, 0)

      };

    });

    return { experimentId: experiment.id, status: experiment.status, variants: rows };

  }

 

  function resetAssignments(experimentId) {

    if (!experimentId) {

      assignments.clear();

      exposures.clear();

      conversions = [];

    } else {

      var id = String(experimentId);

      assignments = new Map(Array.from(assignments.entries()).filter(function (entry) { return entry[1].experimentId !== id; }));

      exposures = new Map(Array.from(exposures.entries()).filter(function (entry) { return entry[1].experimentId !== id; }));

      conversions = conversions.filter(function (item) { return item.experimentId !== id; });

    }

    saveAssignments();

    emit("experiment-data-reset", { experimentId: experimentId || null });

    return true;

  }

 

  function clearStorage() {

    resetAssignments();

    removeStore("local", options.storageKey);

    removeStore("session", options.sessionKey);

    return true;

  }

 

  function start(id) {

    var experiment = registry.get(String(id));

    if (!experiment) return false;

    experiment.status = "running";

    emit("experiment-started", { experimentId: experiment.id });

    return true;

  }

 

  function stop(id) {

    var experiment = registry.get(String(id));

    if (!experiment) return false;

    experiment.status = "stopped";

    emit("experiment-stopped", { experimentId: experiment.id });

    return true;

  }

 

  function pause(id) {

    var experiment = registry.get(String(id));

    if (!experiment) return false;

    experiment.status = "paused";

    emit("experiment-paused", { experimentId: experiment.id });

    return true;

  }

 

  function diagnostics() {

    return {

      name: ENGINE_NAME,

      version: VERSION,

      initialized: initialized,

      enabled: Boolean(options.enabled),

      healthy: initialized,

      experimentCount: registry.size,

      assignmentCount: assignments.size,

      exposureCount: exposures.size,

      conversionCount: conversions.length,

      dependencies: {

        state: Boolean(root.state),

        events: Boolean(root.events),

        analytics: Boolean(root.analytics),

        ruleEngine: Boolean(root.ruleEngine),

        personalizationEngine: Boolean(root.personalizationEngine)

      }

    };

  }

 

  function init(config) {

    options = merge({}, DEFAULTS, config || {});

    hydrate();

    initialized = true;

    emit("experiment-engine-ready", diagnostics());

    return api;

  }

 

  function destroy() {

    initialized = false;

    emit("experiment-engine-destroyed", {});

    return true;

  }

 

  var api = {

    version: VERSION,

    dependencies: ["DHCommerce.state"],

    init: init,

    destroy: destroy,

    register: register,

    registerMany: registerMany,

    remove: remove,

    get: get,

    getAll: getAll,

    clear: clearRegistry,

    assign: assign,

    expose: expose,

    getAssignment: getAssignment,

    getAssignments: getAssignments,

    recordConversion: recordConversion,

    trackEvent: trackEvent,

    summary: summary,

    start: start,

    stop: stop,

    pause: pause,

    resetAssignments: resetAssignments,

    clearStorage: clearStorage,

    hydrate: hydrate,

    diagnostics: diagnostics,

    health: diagnostics

  };

 

  root.experimentEngine = api;

})(typeof window !== "undefined" ? window : globalThis);

